/**
 * Bundle 79 - My Connections view (authed).
 *
 * URL: /connections
 *
 * Shows every mutual connection you have. Reveals ONLY the contact
 * methods the other party opted to share, and only when both sides
 * have accepted.
 *
 * No em/en dashes, no eslint-disable, no photo/image alt.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { UserCircle2, Mail, Phone, MessageCircle, Instagram, LockKeyhole } from 'lucide-react';

const MyConnectionsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/connections');
      setRows(data?.connections || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load connections');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action, reason = '') => {
    try {
      await api.post(`/connections/${id}/${action}`, { reason });
      toast.success(`Connection ${action}.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || `Could not ${action}`); }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="my-connections-page">
      <div className="mb-5">
        <div className="text-[11px] uppercase tracking-widest text-secondary-800">Bundle 79 * Mutual connections</div>
        <h1 className="font-display text-3xl font-bold text-primary-900 mt-1">Your connections</h1>
        <p className="text-neutral-600 text-sm mt-1">Only the contact methods each person agreed to share are visible below.</p>
      </div>
      {loading ? <div className="text-neutral-500 text-sm">Loading...</div> : null}
      {(!loading && rows.length === 0) ? (
        <Card><CardContent className="p-6 text-neutral-500 text-sm text-center">No connections yet. When someone at a Dostination event says hello back, they will appear here.</CardContent></Card>
      ) : null}
      <div className="space-y-3">
        {rows.map((r) => (
          <Card key={r.id} data-testid={`connection-row-${r.id}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-500">
                    <UserCircle2 className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="font-semibold text-primary-900">
                      {r.revealed_from_other?.dost_profile?.first_name} {r.revealed_from_other?.dost_profile?.last_name || ''}
                    </div>
                    <div className="text-xs text-neutral-500">
                      Met at {r.event_where_met} * {new Date(r.connection_date).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <Badge variant="outline" className="capitalize">{r.source}</Badge>
              </div>
              <div className="grid gap-2 text-sm sm:grid-cols-2">
                {r.revealed_from_other?.email ? (
                  <div className="flex items-center gap-2" data-testid={`connection-shared-email-${r.id}`}>
                    <Mail className="h-3.5 w-3.5 text-neutral-500" />
                    <a href={`mailto:${r.revealed_from_other.email}`} className="text-secondary-800 underline">{r.revealed_from_other.email}</a>
                  </div>
                ) : null}
                {r.revealed_from_other?.phone ? (
                  <div className="flex items-center gap-2" data-testid={`connection-shared-phone-${r.id}`}>
                    <Phone className="h-3.5 w-3.5 text-neutral-500" />
                    <a href={`tel:${r.revealed_from_other.phone}`} className="text-secondary-800 underline">{r.revealed_from_other.phone}</a>
                  </div>
                ) : null}
                {r.revealed_from_other?.whatsapp ? (
                  <div className="flex items-center gap-2" data-testid={`connection-shared-whatsapp-${r.id}`}>
                    <MessageCircle className="h-3.5 w-3.5 text-neutral-500" />
                    <span>{r.revealed_from_other.whatsapp}</span>
                  </div>
                ) : null}
                {r.revealed_from_other?.instagram ? (
                  <div className="flex items-center gap-2" data-testid={`connection-shared-instagram-${r.id}`}>
                    <Instagram className="h-3.5 w-3.5 text-neutral-500" />
                    <span>@{r.revealed_from_other.instagram}</span>
                  </div>
                ) : null}
                {(!r.revealed_from_other?.email && !r.revealed_from_other?.phone && !r.revealed_from_other?.whatsapp && !r.revealed_from_other?.instagram) ? (
                  <div className="flex items-center gap-2 text-neutral-500" data-testid={`connection-inside-only-${r.id}`}>
                    <LockKeyhole className="h-3.5 w-3.5" />
                    Communication stays inside Dostination for this connection.
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-neutral-100">
                <Button size="sm" variant="ghost" disabled data-testid={`connection-message-btn-${r.id}`}>
                  Message (coming soon)
                </Button>
                <div className="ml-auto flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => act(r.id, 'close')} data-testid={`connection-close-btn-${r.id}`}>Close</Button>
                  <Button size="sm" variant="outline" onClick={() => act(r.id, 'block')} data-testid={`connection-block-btn-${r.id}`}>Block</Button>
                  <Button size="sm" variant="outline" onClick={() => act(r.id, 'report', 'reported from My Connections')} data-testid={`connection-report-btn-${r.id}`}>Report</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default MyConnectionsPage;
